import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Icon, Text } from "orcs-design-system";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import SuggestedPrompts from "./SuggestedPrompts";
import { useAIChat } from "./context/AIChatContext";
import {
  AGENTCORE_NAMES,
  ASK_TEAMFORM_MODES,
  ASK_TEAMFORM_MODE_UI,
  isAskTeamformV2Agent,
} from "./consts";

const WelcomeContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 16px;
`;

const IconCircle = styled(Box)`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${themeGet("colors.surfaceRaised")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.colors.primary};
  font-size: 60px;
`;

const GreetingText = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.bodyText};
`;

const QuestionText = styled(Text)`
  font-size: 18px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.subText};
  margin-bottom: 24px;
`;

/**
 * AIChatWelcome component - displays the welcome screen with user greeting and suggested prompts
 */
const AIChatWelcome = ({
  userName,
  onPromptSelect,
  showSuggestions = true,
  askTeamformMode: askTeamformModeProp,
  enableSupportQuestionInAskTeamform: enableSupportFlagProp,
  selectedAgent: selectedAgentProp,
}) => {
  const chatContext = useAIChat();
  const askTeamformMode =
    askTeamformModeProp ??
    chatContext.askTeamformMode ??
    ASK_TEAMFORM_MODES.DATA;
  const enableSupportQuestionInAskTeamform =
    enableSupportFlagProp ??
    chatContext.enableSupportQuestionInAskTeamform ??
    false;
  const selectedAgent =
    selectedAgentProp ??
    chatContext.selectedAgent ??
    AGENTCORE_NAMES.ASK_TEAMFORM;

  const welcomeTitle = useMemo(() => {
    if (
      enableSupportQuestionInAskTeamform &&
      isAskTeamformV2Agent(selectedAgent)
    ) {
      return (
        ASK_TEAMFORM_MODE_UI[askTeamformMode]?.welcomeTitle ||
        ASK_TEAMFORM_MODE_UI[ASK_TEAMFORM_MODES.DATA].welcomeTitle
      );
    }
    return "How can TeamForm help?";
  }, [askTeamformMode, enableSupportQuestionInAskTeamform, selectedAgent]);

  return (
    <WelcomeContainer>
      <IconCircle>
        <StyledIcon icon={["fas", "comments-alt"]} />
      </IconCircle>

      <Flex flexDirection="column" alignItems="center" gap="xs" mb="r">
        <GreetingText>Hey {userName}</GreetingText>
      </Flex>
      <QuestionText>{welcomeTitle}</QuestionText>

      {showSuggestions && (
        <SuggestedPrompts
          onPromptSelect={onPromptSelect}
          askTeamformMode={askTeamformMode}
          enableSupportQuestionInAskTeamform={
            enableSupportQuestionInAskTeamform
          }
          selectedAgent={selectedAgent}
        />
      )}
    </WelcomeContainer>
  );
};

AIChatWelcome.propTypes = {
  userName: PropTypes.string.isRequired,
  onPromptSelect: PropTypes.func.isRequired,
  showSuggestions: PropTypes.bool,
  askTeamformMode: PropTypes.string,
  enableSupportQuestionInAskTeamform: PropTypes.bool,
  selectedAgent: PropTypes.string,
};

export default AIChatWelcome;
